/**
 * Command palette recognition.
 *
 * Rules run on the raw string with no prefix required, in this order. The order matters:
 * a 14 digit string is a tracking number, not an order number, and ELU-7Q3K2 is a ticket
 * while ELU-GP3-RT-30MG is a SKU.
 *
 * Pure and exported on its own so the rules are testable without rendering anything.
 */
export type PaletteKind =
  | 'command'
  | 'tracking'
  | 'order'
  | 'ticket'
  | 'customer'
  | 'stock'
  | 'fuzzy';

export interface Recognition {
  kind: PaletteKind;
  /** The raw input, trimmed. */
  query: string;
  /** The identifier the target route wants, with decoration removed. */
  value: string;
}

const ORDER_HASH = /^#\s*(\d{1,10})$/;
const ORDER_DIGITS = /^\d{4,6}$/;
const TRACKING_LONG = /^\d{12,22}$/;
const TRACKING_PREFIX = /^(1Z[0-9A-Z]{10,}|9400\d{6,}|9205\d{6,})$/i;
const TICKET = /^ELU-[0-9A-Z]{5}$/i;
const SKU = /^[A-Z0-9]{2,}(?:-[A-Z0-9]+){1,4}$/i;
const NAME = /^[\p{L}][\p{L}'.-]*(?:\s+[\p{L}'.-]+)+$/u;

export function recognize(raw: string): Recognition {
  const query = raw.trim();
  const value = query;

  if (query.startsWith('>')) {
    return { kind: 'command', query, value: query.slice(1).trim().toLowerCase() };
  }

  const compact = query.replace(/\s+/g, '');

  if (TRACKING_PREFIX.test(compact) || TRACKING_LONG.test(compact)) {
    return { kind: 'tracking', query, value: compact.toUpperCase() };
  }

  const hashed = ORDER_HASH.exec(compact);
  if (hashed?.[1]) {
    return { kind: 'order', query, value: hashed[1] };
  }
  if (ORDER_DIGITS.test(compact)) {
    return { kind: 'order', query, value: compact };
  }

  if (TICKET.test(compact)) {
    return { kind: 'ticket', query, value: compact.toUpperCase() };
  }

  if (query.includes('@')) {
    return { kind: 'customer', query, value: query };
  }

  if (SKU.test(compact)) {
    return { kind: 'stock', query, value: compact.toUpperCase() };
  }

  if (NAME.test(query)) {
    return { kind: 'customer', query, value: query };
  }

  return { kind: 'fuzzy', query, value };
}

export interface PaletteResult {
  id: string;
  group: string;
  label: string;
  detail?: string;
  /** Route to navigate to. Enter opens it. */
  to?: string;
  search?: Record<string, string>;
  /** A command that touches production opens its confirm dialog before it runs. */
  production?: boolean;
  run?: () => void;
}

/** Section names, so every page is reachable by typing its name. */
export const PAGES: readonly { label: string; to: string; keywords: string }[] = [
  { label: 'Home', to: '/', keywords: 'home health inbox today' },
  { label: 'Action Inbox', to: '/inbox', keywords: 'inbox actions alerts' },
  { label: 'Orders', to: '/orders', keywords: 'orders queue print pack ship' },
  { label: 'Stock', to: '/stock', keywords: 'stock inventory sku days left' },
  { label: 'Stock: Inbound', to: '/stock/inbound', keywords: 'inbound po purchase order' },
  { label: 'Stock: Suppliers', to: '/stock/suppliers', keywords: 'suppliers vendor lead time' },
  { label: 'Stock: Pricing', to: '/stock/pricing', keywords: 'source pricing supplier price' },
  { label: 'Customers', to: '/customers', keywords: 'customers people rr balance' },
  { label: 'Support', to: '/support', keywords: 'support tickets queue' },
  { label: 'Support: Handoffs', to: '/support/handoffs', keywords: 'handoff live chat ellie' },
  { label: 'Finance', to: '/finance', keywords: 'finance money waterfall net' },
  { label: 'Finance: Forecast', to: '/finance/forecast', keywords: 'cash forecast runway' },
  { label: 'Finance: Ledgers', to: '/finance/ledgers', keywords: 'fixed costs held funds tax' },
  { label: 'Growth', to: '/growth', keywords: 'growth acquisition cac roas' },
  { label: 'Growth: Partners', to: '/growth/partners', keywords: 'oasis partner attribution' },
  { label: 'Settings: Controls', to: '/settings', keywords: 'gate bogo controls toggles' },
  { label: 'Settings: Users', to: '/settings/users', keywords: 'users devices passkeys pin' },
  { label: 'Settings: Alerts', to: '/settings/alerts', keywords: 'alerts slack sms routing' },
  { label: 'Settings: System', to: '/settings/system', keywords: 'system health jobs crons' },
  { label: 'Settings: Audit', to: '/settings/audit', keywords: 'audit log who did what' },
  { label: 'Settings: Changelog', to: '/settings/changelog', keywords: 'changelog deploys' },
];

export interface CommandSpec {
  id: string;
  label: string;
  keywords: string;
  to?: string;
  /** True when running it changes the live storefront. Confirm first, always. */
  production?: boolean;
}

/** The `>` commands. Production ones land on their control, which owns the ceremony. */
export const COMMANDS: readonly CommandSpec[] = [
  { id: 'gate', label: 'Gate: open the control', keywords: 'gate login enter', to: '/settings', production: true },
  { id: 'bogo', label: 'BOGO: open the control', keywords: 'bogo sale', to: '/settings', production: true },
  { id: 'print-to-print', label: 'Print the to-print queue', keywords: 'print queue labels', to: '/orders', production: false },
  { id: 'new-po', label: 'New purchase order', keywords: 'po purchase order supplier', to: '/stock/inbound' },
  { id: 'issue-credit', label: 'Issue store credit', keywords: 'credit rr rewards', to: '/customers' },
  { id: 'oasis', label: 'Oasis partner attribution', keywords: 'oasis partner cpa', to: '/growth/partners' },
  { id: 'audit', label: 'Audit log', keywords: 'audit log', to: '/settings/audit' },
  { id: 'lock', label: 'Lock the console', keywords: 'lock session', to: '/lock' },
];

function score(haystack: string, needle: string): number {
  if (!needle) return 0.1;
  const lowerHay = haystack.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  if (lowerHay === lowerNeedle) return 100;
  if (lowerHay.startsWith(lowerNeedle)) return 80;
  if (lowerHay.includes(lowerNeedle)) return 60;
  // Subsequence match, so "stinb" finds "Stock: Inbound".
  let index = 0;
  for (const char of lowerNeedle) {
    index = lowerHay.indexOf(char, index);
    if (index === -1) return 0;
    index += 1;
  }
  return 20;
}

/**
 * Builds the result list for a raw input. The recognized target is always first; page names
 * and commands follow. Async providers (an order lookup, a customer search) are registered
 * by the owning agent and merged by the palette component.
 */
export function buildResults(raw: string): PaletteResult[] {
  const recognition = recognize(raw);
  const results: PaletteResult[] = [];

  switch (recognition.kind) {
    case 'order':
      results.push({
        id: `order:${recognition.value}`,
        group: 'Order',
        label: `Order ${recognition.value}`,
        detail: 'Open the order',
        to: `/orders/${recognition.value}`,
      });
      break;
    case 'ticket':
      results.push({
        id: `ticket:${recognition.value}`,
        group: 'Support',
        label: `Ticket ${recognition.value}`,
        detail: 'Open the ticket workspace',
        to: `/support/${recognition.value}`,
      });
      break;
    case 'tracking':
      results.push({
        id: `tracking:${recognition.value}`,
        group: 'Tracking',
        label: `Tracking ${recognition.value}`,
        detail: 'Jump to the order carrying this number',
        to: '/orders',
        search: { q: recognition.value },
      });
      break;
    case 'customer':
      results.push({
        id: `customer:${recognition.value}`,
        group: 'Customer',
        label: `Customers matching "${recognition.value}"`,
        to: '/customers',
        search: { q: recognition.value },
      });
      break;
    case 'stock':
      results.push({
        id: `stock:${recognition.value}`,
        group: 'Stock',
        label: `SKU ${recognition.value}`,
        to: '/stock',
        search: { q: recognition.value },
      });
      break;
    case 'command': {
      for (const command of COMMANDS) {
        const rank = Math.max(
          score(command.label, recognition.value),
          score(command.keywords, recognition.value),
          score(command.id, recognition.value),
        );
        if (rank > 0) {
          results.push({
            id: `command:${command.id}`,
            group: 'Command',
            label: command.label,
            detail: command.production ? 'Touches the live storefront' : undefined,
            ...(command.to ? { to: command.to } : {}),
            production: command.production === true,
          });
        }
      }
      return results;
    }
    case 'fuzzy':
    default:
      break;
  }

  const needle = recognition.kind === 'fuzzy' ? recognition.query : '';
  const pages = PAGES.map((page) => ({
    page,
    rank: Math.max(score(page.label, needle), score(page.keywords, needle)),
  }))
    .filter((entry) => entry.rank > 0)
    .sort((a, b) => b.rank - a.rank)
    .slice(0, 8);

  for (const entry of pages) {
    results.push({
      id: `page:${entry.page.to}`,
      group: 'Go to',
      label: entry.page.label,
      to: entry.page.to,
    });
  }

  if (recognition.kind === 'fuzzy' && recognition.query.length >= 2) {
    results.push({
      id: `search:${recognition.query}`,
      group: 'Search',
      label: `Search orders for "${recognition.query}"`,
      to: '/orders',
      search: { q: recognition.query },
    });
  }

  return results;
}

const RECENTS_KEY = 'ops.palette.recents';
const RECENTS_MAX = 8;

export function readRecents(): PaletteResult[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PaletteResult[];
    return Array.isArray(parsed) ? parsed.slice(0, RECENTS_MAX) : [];
  } catch {
    return [];
  }
}

export function pushRecent(result: PaletteResult): void {
  try {
    const current = readRecents().filter((entry) => entry.id !== result.id);
    const next = [{ ...result, group: 'Recent' }, ...current].slice(0, RECENTS_MAX);
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // Recents are a convenience. A browser refusing storage is not an error worth showing.
  }
}
